import type { FormInstance } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LocationSelect } from 'app/components/commons/LocationSelect/LocationSelect';
import { HeaderWrapper } from 'app/components/pages/Edito/commons/Detail/HeaderWrapper/HeaderWrapper';
import type { Coordinates } from 'app/redux/models/Coordinates/Coordinates';
import type { IEditoEventForm } from 'app/typings/edito';
import { placeFromCoords } from 'app/utils/googlePlace/googlePlace';

import './LocationForm.scss';

type Props = {
  fieldName: string | number;
  form: FormInstance<IEditoEventForm>;
};

export const LocationForm = ({ fieldName, form }: Props) => {
  const [initialValue, setInitialValue] = useState('');

  const isEndingBlock = fieldName === 'endingBlock';

  const basePathName = useMemo(
    () => (isEndingBlock ? [fieldName] : ['sections', fieldName]),
    [fieldName, isEndingBlock]
  );

  const setPlace = useCallback(
    async (coords: Coordinates) => {
      const place = await placeFromCoords(coords);

      if (place) {
        form.setFieldValue(
          [...basePathName, 'searchFilters', 'placeId'],
          place.place_id
        );

        const addressComponents = place.address_components.filter((address) =>
          address.types.includes('locality')
        );

        if (addressComponents.length) {
          const locality = addressComponents[0].long_name;

          setInitialValue(locality);

          form.setFieldValue(
            [...basePathName, 'searchFilters', 'location'],
            locality
          );
        }
      }
    },
    [basePathName, form]
  );

  useEffect(() => {
    const coords = form.getFieldValue([
      ...basePathName,
      'searchFilters',
      'coords',
    ]);

    if (coords) {
      setPlace(coords);
    }
  }, [basePathName, form, setPlace]);

  return (
    <div className="locationForm">
      <HeaderWrapper isBold={false} title="Location">
        <LocationSelect
          renderLocationLabel={(location) => (
            <div>
              <span>{location.text}</span>
              <span>{location.subtext && ` (${location.subtext})`}</span>
            </div>
          )}
          formItemProps={{
            name: [fieldName, 'searchFilters', 'placeId'],
          }}
          initialValue={initialValue}
          placePredictionTypes={['locality']}
          minCharacters={0}
          placeholder="Search for a location"
        />
      </HeaderWrapper>
    </div>
  );
};
